// src/app/top-bar-routes.ts
// The top bar's route knowledge, kept out of the component file (fast refresh): which pathnames
// are top-level sections, the structural breadcrumb for a pathname, and what the search finds on
// each screen. Design log 12–13 Oct: the breadcrumb says where a screen sits in the app, not the
// path taken; the Dashboard (the root) shows no lone crumb; inside a patient it is "Patients › <name>", and in the
// encounter "Today › <name>" (Antonin's export, 22 Oct, encounterFrame: Today lit, the visit opened from the day).
import { matchPath } from 'react-router-dom';
import { homePath } from '@/shared/lib/home-path';

export interface Crumb {
  label: string;
  /** Present on every crumb except the last, which is the current page. */
  to?: string;
  /** Navigation state for the link (the Settings tab the crumb returns to). */
  state?: unknown;
}

/** The top-level sections: one crumb each, and the pages Back walks between. */
export const SECTIONS: Record<string, string> = {
  dashboard: 'Dashboard',
  today: 'Today',
  calendar: 'Calendar',
  patients: 'Patients',
  settings: 'Settings',
};

const PATIENTS: Crumb = { label: 'Patients', to: '/patients' };

/** The top-level section a pathname IS (not merely sits under); null for nested screens. */
export function sectionOf(pathname: string): string | null {
  const m = /^\/([^/]+)\/?$/.exec(pathname);
  return m && m[1] in SECTIONS ? m[1] : null;
}

/** The breadcrumb trail for a pathname; empty on the Dashboard and on a route the map does not name. */
export function crumbsFor(pathname: string, ownName?: string | null): Crumb[] {
  const section = sectionOf(pathname);
  if (section) return section === 'dashboard' ? [] : [{ label: SECTIONS[section] }];
  const name = ownName?.trim() || 'Patient';
  if (matchPath('/patients/new', pathname)) return [PATIENTS, { label: 'New patient' }];
  if (matchPath('/patients/edit/:patientId', pathname)) return [PATIENTS, { label: name }];
  // Review results (design intake-review.html crumbs: Patients › name › Review results). The name links back to the
  // patient's Documents, where the reading came from (the page's own breadcrumb used to).
  const intake = matchPath('/patients/edit/:patientId/intake/:runId', pathname);
  if (intake)
    return [
      PATIENTS,
      { label: name, to: `/patients/edit/${intake.params.patientId}?tab=chart&inner=documents` },
      { label: 'Review results' },
    ];
  if (matchPath('/encounter/:appointmentId', pathname)) return [{ label: SECTIONS.today, to: homePath() }, { label: name }];
  if (matchPath('/scheduling/appointment/:appointmentId', pathname))
    return [{ label: 'Calendar', to: '/calendar' }, { label: ownName?.trim() || 'Appointment' }];
  if (matchPath('/billing/master-data/provider-fees/:providerId', pathname))
    return [
      { label: 'Settings', to: '/settings' },
      { label: 'Provider fees', to: '/settings', state: { tab: 'billing' } },
      { label: ownName?.trim() || "Provider's Fees" },
    ];
  return [];
}


/** What the search finds, said for the screen it sits on. */
export function searchPlaceholder(pathname: string): string {
  if (pathname === '/patients') return 'Filter patients by name, MRN or phone';
  if (pathname.startsWith('/patients/') || pathname.startsWith('/encounter/'))
    return 'Find another patient by name, MRN or phone';
  return 'Search patients by name, MRN or phone';
}
